import moment from 'moment'
import _ from 'lodash'
import Avatar from '@mui/material/Avatar'
import { Image } from 'react-bootstrap'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import { fileUrl } from 'api/api'

/** COMPONENT */
import AutocompleteSelect from 'component/Select/AutocompleteSelect'
import Box from '@mui/material/Box'

/** UTILS */
import { getMember } from 'utils/app.utils'

/** CONSTANTS */
import { ThemeIcon, colors } from 'constants/theme'
import { icons, imgs } from 'constants/images';

/** STYLE */
import { styled } from '@mui/material/styles'
import { useCallback, useMemo, useState } from 'react'
import { BoxStatus, TextStatus } from './UseStyled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser } from '@fortawesome/free-solid-svg-icons'

const AutoCompleteWrap = styled('div')(({ theme }) => ({}))

type FilterPatientType = {
  data: any
  onchange: (event: any, value: any) => void
  disabled?: boolean
  branch: any
}

const SELECT_ALL_OPTIONS = [{ appointmentId: 0, cnNumber: '' }]

const getPatientCode = (option: any, branch: any): string | undefined => {
  if (branch?.patientCodeAppointment === '1') return option.cnNumber
  if (branch?.patientCodeAppointment === '0') return option.branchCnNumber
  if (branch?.patientCodeAppointment === '2') return option.snCode
  return undefined
}

const renderImage = (img: any) => {
  if (!_.isEmpty(img)) return `${fileUrl}/${img}`
  return imgs.defaultAvatar
}

export default function FilterPatient(props: FilterPatientType) {
  const { t } = useTranslation()
  const { branch, onchange: onchangeProp, data, disabled } = props
  const [selected, setSelected]: any = useState({ appointmentId: 0, cnNumber: '' });

  const statusIcon: any = useMemo(() => ({
    NORMAL: { icon: <FontAwesomeIcon style={{ color: colors.white }} icon={faUser} />, color: colors.appointmentStatus.statusNormal, label: t('APPOINTMENT.STATUS.NORMAL'), disabled: false },
    CANCEL_APP: { icon: <Image style={{ height: '15px' }} src={icons.appointment.cancel_app} />, color: colors.appointmentStatus.statusCancel, label: t('APPOINTMENT.STATUS.CANCEL'), disabled: false },
    CANCEL: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconCancel} />, color: colors.appointmentStatus.statusCancel, label: t('APPOINTMENT.STATUS.CANCEL'), disabled: false },
    CAN_NOT_CONTACT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconNotConnect} />, color: colors.appointmentStatus.statusUnConnect, label: t('APPOINTMENT.STATUS.CAN_NOT_CONTACT'), disabled: false },
    PUT_OFF: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconChangeSchedule} />, color: colors.appointmentStatus.statusChange, label: t('APPOINTMENT.STATUS.PUT_OFF'), disabled: false },
    CONFIRM: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconEnter} />, color: colors.appointmentStatus.statusEnter, label: t('APPOINTMENT.STATUS.CONFIRM'), disabled: false },
    ARRIVED: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconUserCheckTrue} />, color: colors.appointmentStatus.statusArrived, label: t('APPOINTMENT.STATUS.ARRIVED'), disabled: false },
    NOT_YET: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconUserCheckFalse} />, color: colors.appointmentStatus.statusUnArrived, label: t('APPOINTMENT.STATUS.NOT_YET'), disabled: false },
    PAYMENT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconPendingPayment} />, color: colors.appointmentStatus.statusWaitPayment, label: t('APPOINTMENT.STATUS.PAYMENT'), disabled: true },
    PENDING_PAYMENT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconWaitPayment} />, color: colors.appointmentStatus.statusPayment, label: t('APPOINTMENT.STATUS.PENDING_PAYMENT'), disabled: true },
    PENDING_SERVICE: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconCounterCheckIn} />, color: colors.appointmentStatus.statusCounterCheckIn, label: t('APPOINTMENT.STATUS.PENDING_SERVICE'), disabled: true },
    SERVICE: { icon: <Image style={{ height: '17px' }} src={icons.appointment.iconService} />, color: colors.appointmentStatus.statusService, label: t('APPOINTMENT.STATUS.SERVICE'), disabled: true },
    DONE: { icon: <Image style={{ height: '15px' }} src={ThemeIcon.DoneTreatment} />, color: colors.appointmentStatus.statusDone, label: t('APPOINTMENT.STATUS.DONE'), disabled: true },
  }), [t])

  const memberAll = useMemo(() => getMember(), [])

  const renderAppointStatus = useCallback((status: string) => (
    <BoxStatus style={{ marginLeft: '10px' }} bg={statusIcon[status]?.color || ''}>
      {statusIcon[status]?.icon || ''}
      <TextStatus>{statusIcon[status]?.label || ''}</TextStatus>
    </BoxStatus>
  ), [statusIcon])

  const options = useMemo(() => [...SELECT_ALL_OPTIONS, ...(data || [])], [data])

  const handleOpen = useCallback(() => setSelected({ appointmentId: '', cnNumber: '' }), [])

  const renderOption = useCallback((autoProps: any, option: any) => {
    if (option.appointmentId === 0) {
      return (
        <Box key="All" component="li" {...autoProps}>
          <p className={'m-0'} style={{ fontWeight: 500 }}>
            {t('HOME.TITLE.SELECT_SCHEDULE')}
          </p>
        </Box>
      )
    }
    const patientCode = getPatientCode(option, branch)
    return (
      <Box component="li" {...autoProps}>
        <Avatar alt={option.patientFullname} src={renderImage(option.patientProfile)} sx={{ width: 40, height: 40, border: `2px solid ${_.find(memberAll, { rankId: option.rankId })?.color}`, backgroundColor: colors.white }}>
          <img src={imgs.defaultAvatar} alt="defaultAvatar" className="w-100 h-100" />
        </Avatar>
        <div className={'ml-3'}>
          <p className={'mb-0'}>
            {patientCode ? `${patientCode} |` : ''} {option.patientFullname}
          </p>
          <p className={'mb-0 d-flex align-item-center'} style={{ fontSize: 13, color: colors.disabledGray }}>
            {option.doctorFullname} {moment(option.timeStart, 'HH:mm').format('HH.mm')} {t('TIME_UNIT')}
            {renderAppointStatus(option.statusAppointment)}
          </p>
        </div>
      </Box>
    )
  }, [t, branch, memberAll, renderAppointStatus])

  const getOptionLabel = useCallback((option: any) => {
    if (option.appointmentId === '') return ''
    if (option.appointmentId === 0) return `${t('HOME.TITLE.SELECT_SCHEDULE')}`
    const patientCode = getPatientCode(option, branch)
    return `${patientCode ? `${patientCode} |` : ''} ${option.patientFullname} (${option.doctorFullname} ${moment(option.timeStart, 'HH:mm').format('HH.mm')} ${t('TIME_UNIT')})`
  }, [t, branch])

  const handleChange = useCallback((e: any, value: any) => {
    setSelected(value)
    onchangeProp(e, value)
  }, [onchangeProp])

  const filterOptions = useCallback(
    (option: any) => option.cnNumber + option.branchCnNumber + option.patientFullname + option.doctorFullname,
    []
  )

  return (
    <AutoCompleteWrap>
      <AutocompleteSelect
        labelId="filter-patient"
        inputLabel={''}
        onOpen={handleOpen}
        options={options}
        renderOption={renderOption}
        value={selected}
        defaultValue={selected}
        getOptionLabel={getOptionLabel}
        onchange={handleChange}
        filterOptions={filterOptions}
        noOptionsText={t('HOME.TITLE.FILTER_NOT_FOUND')}
        classesOption={'indexBelowSidebar'}
        disableClearable
        disabledBorder
        disabled={disabled}
      ></AutocompleteSelect>
    </AutoCompleteWrap>
  )
}
